import * as React from 'react';
import { useInView } from 'react-intersection-observer';
import { FormattedMessage, useIntl } from 'react-intl';

import { useQuery } from '@apollo/client';

import {
  DrawerContext,
  ModalContext,
  ModalModel,
  useBreakpointValue,
} from '@notino/react-styleguide';
import {
  GetProductImagesQuery,
  GetProductImagesQueryVariables,
  ProductIdType,
} from '@notino/shared/definitions/types';

import { useDrawer } from '@components/BottomSheet/DrawerProvider';
import { trackDrawerClose } from '@components/BottomSheet/tracking';
import { useTrackingContext } from '@context/tracking/TrackingContext';

import { PopupBox } from '../../../../../components/PopupBox';
import getProductImagesQuery from '../../../../../queries/getProductsImages.graphql';
import { useProductDetailContext } from '../../../ProductDetailContext';
import { trackElementClick } from '../hooks/useTrackExtras';

import { AvailableSetsList } from './AvailableSetsList';
import { messages } from './messages';
import { DiscoverSetsText } from './styled';
import { getDiscoverSetsMessage } from './utils';

const AvailableSets = () => {
  const { inView, ref } = useInView({ triggerOnce: true });
  const isDesktop = useBreakpointValue({ md: true });
  const { toggleModal, hideModal } = ModalContext.useModalContext();
  const { toggleDrawer } = useDrawer();
  const { getTimeFromInit } = useTrackingContext();
  const { formatMessage } = useIntl();
  const { product, currentVariant } = useProductDetailContext();
  const productAvailableSets = product.features?.availableSets;
  const variantAvailableSets = currentVariant.features?.availableSets;

  const sets = productAvailableSets?.sets ?? variantAvailableSets?.sets ?? [];

  const { data, loading } = useQuery<
    GetProductImagesQuery,
    GetProductImagesQueryVariables
  >(getProductImagesQuery, {
    skip: sets.length === 0,
    variables: {
      ids: [String(sets[0]?.catalogId)],
      idType: ProductIdType.Catalog,
    },
    ssr: false,
  });

  if (sets.length === 0) {
    return null;
  }

  const openAvailableSets = () => {
    trackElementClick({
      timing: getTimeFromInit(),
      action: 'gif_sets_open',
      type: 'gift_sets',
      name: 'gift_sets_open',
      interaction: 'click',
    });

    if (isDesktop) {
      toggleModal(
        // DrawerContext.Provider is used to be able to render wishlist list selection drawer from inside modal
        <DrawerContext.Provider>
          <AvailableSetsList sets={sets} />
        </DrawerContext.Provider>,
        {
          size: ModalModel.ModalSizes.large,
          withFocusTrap: true,
          onClose: () => {
            trackElementClick({
              timing: getTimeFromInit(),
              name: 'gift_sets_close',
              type: 'gift_sets',
              action: 'gift_sets_close',
            });
            hideModal();
          },
        }
      );
      return;
    }
    toggleDrawer(<AvailableSetsList sets={sets} />, {
      onDismiss: (closeType) =>
        trackDrawerClose({
          closeType,
          timing: getTimeFromInit(),
          name: 'gift_sets_close',
          type: 'gift_sets',
          action: 'gift_sets_close',
        }),
      header: formatMessage(messages.setsWithThisProduct),
      ...(sets.length >= 3 && { ignoreBodySwipe: true }),
    });
  };

  const imageUrl = inView && !loading && data?.vpProductByIds[0]?.imageUrl;

  return (
    <PopupBox
      ref={ref}
      dataTestId="available-sets-wrapper"
      cdnImageUrl={imageUrl || undefined}
      onClick={openAvailableSets}
    >
      <DiscoverSetsText>
        <FormattedMessage {...getDiscoverSetsMessage(sets)} /> ({sets.length})
      </DiscoverSetsText>
    </PopupBox>
  );
};

export default AvailableSets;
